// Authentication service — talks to the FastAPI /auth endpoints.
import { AuthResponse } from '../types/auth.types';
import { API_CONFIG } from "../utils/constants";
import axios from 'axios';

const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    // Ensure headers object exists
    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        const { data } = await apiClient.post<AuthResponse>(API_CONFIG.ENDPOINTS.REFRESH_TOKEN, {
          refreshToken,
        });
        localStorage.setItem("accessToken", data.access_token);
        localStorage.setItem("refreshToken", data.refresh_token || "");
        return apiClient(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // POST /auth/login -> { access_token, user: { id, fname, email, role } }
  login: async (credentials: { email: string; password: string }): Promise<any> => {
    const { data } = await apiClient.post<any>(API_CONFIG.ENDPOINTS.LOGIN, credentials);
    return data;
  },

  // POST /auth/register -> UserOut
  register: async (payload: {
    fname: string;
    email: string;
    mobile: string;
    role: string;
    password: string;
  }): Promise<any> => {
    const { data } = await apiClient.post<any>(API_CONFIG.ENDPOINTS.REGISTER, payload);
    return data;
  },

  // POST /auth/forgot-password -> { message } (sends an OTP to the email)
  forgotPassword: async (payload: { email: string }): Promise<any> => {
    const { data } = await apiClient.post<any>(API_CONFIG.ENDPOINTS.FORGOT_PASSWORD, payload);
    return data;
  },

  // POST /auth/verify-otp -> { message, reset_token }
  verifyOtp: async (payload: { email: string; otp: string }): Promise<any> => {
    const { data } = await apiClient.post<any>(API_CONFIG.ENDPOINTS.VERIFY_OTP, payload);
    return data;
  },

  // POST /auth/reset-password -> { message }
  resetPassword: async (payload: { token: string; new_password: string }): Promise<any> => {
    const { data } = await apiClient.post<any>(API_CONFIG.ENDPOINTS.RESET_PASSWORD, payload);
    return data;
  },

  logout: (): void => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("user");
  },
};

/** Pulls the most useful message out of an axios/FastAPI error. */
export const getAuthErrorMessage = (error: any, fallback = "Something went wrong"): string => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors come back as a list of { msg, loc }.
    return detail.map((d: any) => d.msg || d).join(", ");
  }
  return detail || error?.response?.data?.message || error?.message || fallback;
};